import { CommonForm } from '@/components/CommonForm';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonPhoneInput } from '@/components/CommonForm/CommonPhoneInput';
import { CommonSelect } from '@/components/CommonForm/CommonSelect';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents, isFormDynamicsLocationEnabled } from '@/tools/forms';
import { withSuspense } from '@/tools/withSuspense';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping } from '@/types/dynamicsForms';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RichText } from '@lamesarv-sdk/components';
import { navigate, parsePhone, stateAbbreviationTitles } from '@lamesarv-sdk/tools';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { BaseOptInCheckbox, BaseSubmit, BaseSubscribeCheckbox } from '../base';
import { IFormProps, IFormResetterProps } from '../types';
import { IFormSchema, validationSchema } from './schema';

import * as FormClasses from '../classes';

const formStyles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-3 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-1 group-focus-within:top-1 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-1 top-1 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-3 rounded-sm focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-5 focus:pb-1',
  inputWithValue: 'px-3 pt-5 pb-1',
  button:
    'capitalize rounded-md bg-blue-500 text-white border-none hover:bg-blue-400 hover:text-white hover:border-none',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormContactSell = withSuspense((props: IFormProps) => {
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const getLocationDetails = (location: string) => {
    const [city, stateCode] = location.split(',').map((item) => item.trim());
    const state = stateAbbreviationTitles[stateCode.toUpperCase()] || stateCode;

    return { city, state };
  };

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const { city, state } = getLocationDetails(formInput.location);

    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.buyer) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      { DataverseFieldName: 'lmrv_make', DataverseFieldValue: formInput.make || '' },
      { DataverseFieldName: 'lmrv_vehicleyear', DataverseFieldValue: String(formInput.year ?? '') },
      { DataverseFieldName: 'lmrv_model', DataverseFieldValue: formInput.model || '' },
      { DataverseFieldName: 'lmrv_miles', DataverseFieldValue: String(formInput.miles ?? '') },
      { DataverseFieldName: 'firstname', DataverseFieldValue: formInput.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: formInput.lastName },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: formInput.email },
      { DataverseFieldName: 'address1_postalcode', DataverseFieldValue: formInput.zipCode || '' },
      { DataverseFieldName: 'address1_city', DataverseFieldValue: city },
      { DataverseFieldName: 'address1_stateorprovince', DataverseFieldValue: state },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(props.locationsOptionSet.find((item) => item.Label === formInput.location)?.DataverseValue) || '',
      },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (formInput.subscribe || formInput.smsOptIn) {
      const topicChannels = [formInput.subscribe ? 'Email' : undefined, formInput.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    if (isFormDynamicsLocationEnabled(env, formInput.location)) {
      await sendForm({
        formKey: 'CONTACT_SELL',
        formName: props.custom_name || 'Form Contact Sell',
        mappings,
      });
    }
  };

  const wasSuccessful = !isLoading && !hasError && submitted;

  return (
    <CommonForm
      id={props.custom_id || 'formContactSell'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-contact-sell` : 'form-contact-sell'}
    >
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge('w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-2', props.classes_header)}
        >
          {props.title && <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>}
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-2 bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col gap-4', props.classes_header)}>
            {props.title && (
              <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>
            )}
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        {isLoading && (
          <div
            className={
              'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
            <span>Sending...</span>
          </div>
        )}
        {wasSuccessful && (
          <div
            className={
              'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
            <span
              data-tag-group="form-success-label"
              data-tag={
                props.data_tag
                  ? `${props.data_tag}--form-contact-sell-success-label`
                  : 'form-contact-sell-success-label'
              }
            >
              Thank you for requesting more information, a member of our Buying Team will reach out to you shortly.
            </span>
          </div>
        )}
        {!isLoading && hasError && (
          <div
            className={
              'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
            <span>An error has occurred, please try again later or contact us using alternative methods.</span>
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-row flex-wrap -mx-2">
            <h3 className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Tell Us About Your RV</h3>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput type="text" label="Make" placeholder="Make" name="make" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput type="text" label="Model" placeholder="Model" name="model" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput
                type="number"
                label="Year"
                placeholder="Year"
                name="year"
                customOptions={{ min: 0, step: 1 }}
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput
                type="number"
                label="Miles"
                placeholder="Miles"
                name="miles"
                customOptions={{ min: 0, step: 1 }}
                isRequired
                hideLabel
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap -mx-2">
            <h3 className="my-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Tell Us How We Can Reach You</h3>
            <div className="my-2 px-2 w-full">
              <CommonSelect
                name="contactType"
                label="Select Sell or Trade-In"
                options={[
                  { title: 'Sell', value: 'sell' },
                  { title: 'Trade-In', value: 'trade-in' },
                ]}
                isRequired
                classContainer={formStyles.container}
                classLabel={formStyles.label}
                classLabelWithValue={formStyles.labelWithValue}
                classInput={twMerge(formStyles.input, props.classes_input)}
                classInputWithValue={formStyles.inputWithValue}
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name"
                name="firstName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput type="text" label="Last Name" placeholder="Last Name" name="lastName" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput type="email" label="Email" placeholder="Email" name="email" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonPhoneInput label="Phone" placeholder="Phone" name="phone" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonLocationSelect
                label="Preferred Store"
                name="location"
                locationsOptionSet={props.locationsOptionSet}
                isRequired
                classContainer={formStyles.container}
                classLabel={formStyles.label}
                classLabelWithValue={formStyles.labelWithValue}
                classInput={twMerge(formStyles.input, props.classes_input)}
                classInputWithValue={formStyles.inputWithValue}
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput type="text" label="Zip Code" placeholder="Zip Code" name="zipCode" isRequired hideLabel />
            </div>
          </div>
          {props.display_subscribe_checkbox && (
            <div className="flex flex-row py-2 text-left">
              <BaseSubscribeCheckbox htmlContent={props.custom_subscribe_text} />
            </div>
          )}
          {props.display_sms_checkbox && (
            <div className="flex flex-row py-2">
              <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
            </div>
          )}
          <div className="flex flex-row">
            <div className="mt-2">
              <BaseSubmit {...props} formNameSlug="form-contact-sell" styleClasses={FormClasses.buttonNormalClasses} />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
